import React, { useState, useEffect } from "react";
import { getLogostripSections, createLogostripItem, updateLogostripItem, deleteLogostripItem, uploadImage } from "../../../APIClient/apiClient";
import { resolveImageSrc } from "../../../utils/resolveImageSrc";
import Toast from "../../../component/Toast";
import "../../../style/admin/LogostripManager.css";

const LogostripManager = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [showItemForm, setShowItemForm] = useState(null); // section ID for which to show item form
  const [editingItemId, setEditingItemId] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const [itemFormData, setItemFormData] = useState({
    section_id: null,
    href: "",
    src: "",
    en: { alt: "" },
    vi: { alt: "" },
    is_active: true,
    sort_order: 0,
  });

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    setLoading(true);
    try {
      const data = await getLogostripSections();
      setSections(data);
      // Auto-expand all sections
      setExpandedSections(new Set(data.map((s) => s.id)));
    } catch (err) {
      setToast({ message: "Failed to load logostrip sections", type: "error" });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Item management
  const handleItemInputChange = (language, field, value) => {
    setItemFormData({
      ...itemFormData,
      [language]: {
        ...itemFormData[language],
        [field]: value,
      },
    });
  };

  const handleItemMetaChange = (field, value) => {
    setItemFormData({
      ...itemFormData,
      [field]: value,
    });
  };

  const handleImageFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setToast({ message: "Please select an image file", type: "error" });
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setToast({ message: "Image size must be less than 5MB", type: "error" });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload image
    setUploadingImage(true);
    try {
      const result = await uploadImage(file);
      setItemFormData({
        ...itemFormData,
        src: result.imageUrl,
      });
      setToast({ message: "Image uploaded successfully", type: "success" });
    } catch (err) {
      setToast({ message: err.message || "Failed to upload image", type: "error" });
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItemId) {
        await updateLogostripItem(editingItemId, itemFormData);
      } else {
        await createLogostripItem(itemFormData);
      }
      fetchSections();
      resetItemForm();
      setToast({ message: "Logostrip item saved successfully", type: "success" });
    } catch (err) {
      setToast({ message: err.message || "Failed to save logostrip item", type: "error" });
    }
  };

  const handleDeleteItem = async (id) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        await deleteLogostripItem(id);
        fetchSections();
        setToast({ message: "Item deleted successfully", type: "success" });
      } catch (err) {
        setToast({ message: "Failed to delete item", type: "error" });
      }
    }
  };

  const handleEditItem = (item, sectionId) => {
    setItemFormData({
      section_id: sectionId,
      href: item.href,
      src: item.src,
      en: item.translations.find((t) => t.language === "en") || { alt: "" },
      vi: item.translations.find((t) => t.language === "vi") || { alt: "" },
      is_active: item.is_active,
      sort_order: item.sort_order,
    });
    setImagePreview(item.src); // Show current image
    setEditingItemId(item.id);
    setShowItemForm(sectionId);
  };

  const openItemFormForSection = (sectionId) => {
    setItemFormData({
      section_id: sectionId,
      href: "",
      src: "",
      en: { alt: "" },
      vi: { alt: "" },
      is_active: true,
      sort_order: 0,
    });
    setImagePreview(null);
    setShowItemForm(sectionId);
  };

  const resetItemForm = () => {
    setItemFormData({
      section_id: null,
      href: "",
      src: "",
      en: { alt: "" },
      vi: { alt: "" },
      is_active: true,
      sort_order: 0,
    });
    setEditingItemId(null);
    setShowItemForm(null);
    setImagePreview(null);
  };

  const toggleSection = (sectionId) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  if (loading) return <div className="admin-manager-loading">Loading logostrip...</div>;

  return (
    <div className="admin-manager">
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "success" })} />

      <div className="logostrip-sections-list">
        {sections.map((section) => (
          <div key={section.id} className="logostrip-section-card">
            <div className="section-header">
              <div className="section-info">
                <button className="toggle-btn" onClick={() => toggleSection(section.id)}>
                  {expandedSections.has(section.id) ? "▼" : "▶"}
                </button>
                <strong>{section.slug}</strong>
                <span className="section-title">{section.translations.find((t) => t.language === "en")?.title || "No title"}</span>
                <span className={`status-badge ${section.is_active ? "active" : "inactive"}`}>{section.is_active ? "ACTIVE" : "INACTIVE"}</span>
              </div>
            </div>

            {expandedSections.has(section.id) && (
              <div className="section-content">
                <div className="items-header">
                  <h4>Items ({section.items?.length || 0})</h4>
                  <button className="btn-add-item" onClick={() => openItemFormForSection(section.id)}>
                    Add Item
                  </button>
                </div>

                {showItemForm === section.id && (
                  <div className="item-form">
                    <h5>{editingItemId ? "Edit Item" : "Add New Item"}</h5>
                    <form onSubmit={handleItemSubmit}>
                      <div className="form-group full-width">
                        <label>Href (URL)</label>
                        <input type="text" value={itemFormData.href} onChange={(e) => handleItemMetaChange("href", e.target.value)} required />
                      </div>

                      <div className="form-group full-width">
                        <label>Image Source</label>
                        <div className="image-upload-section">
                          <input
                            type="text"
                            value={itemFormData.src}
                            onChange={(e) => handleItemMetaChange("src", e.target.value)}
                            placeholder="Enter image URL or upload file below"
                            required
                          />
                          <div className="file-upload-wrapper">
                            <label htmlFor="image-upload" className="btn-upload">
                              {uploadingImage ? "Uploading..." : "Or Upload Image"}
                            </label>
                            <input
                              id="image-upload"
                              type="file"
                              accept="image/*"
                              onChange={handleImageFileChange}
                              disabled={uploadingImage}
                              style={{ display: "none" }}
                            />
                          </div>
                          {imagePreview && (
                            <div className="image-preview">
                              <img src={imagePreview} alt="Preview" />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Sort Order</label>
                          <input
                            type="number"
                            value={itemFormData.sort_order}
                            onChange={(e) => handleItemMetaChange("sort_order", parseInt(e.target.value))}
                          />
                        </div>
                        <div className="form-group">
                          <label>Active</label>
                          <input
                            type="checkbox"
                            checked={itemFormData.is_active}
                            onChange={(e) => handleItemMetaChange("is_active", e.target.checked)}
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Alt Text (English)</label>
                          <input
                            type="text"
                            value={itemFormData.en.alt}
                            onChange={(e) => handleItemInputChange("en", "alt", e.target.value)}
                            placeholder="Alt text"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Alt Text (Vietnamese)</label>
                          <input
                            type="text"
                            value={itemFormData.vi.alt}
                            onChange={(e) => handleItemInputChange("vi", "alt", e.target.value)}
                            placeholder="Văn bản thay thế"
                            required
                          />
                        </div>
                      </div>

                      <div className="form-actions">
                        <button type="submit" className="btn-primary">
                          {editingItemId ? "Update" : "Add"} Item
                        </button>
                        <button type="button" className="btn-secondary" onClick={resetItemForm}>
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {section.items && section.items.length > 0 ? (
                  <div className="items-list">
                    {section.items.map((item) => (
                      <div key={item.id} className="item-card">
                        <div className="item-preview">
                          <img src={resolveImageSrc(item.src)} alt={item.translations.find((t) => t.language === "en")?.alt} className="item-img" />
                        </div>
                        <div className="item-details">
                          <div>
                            <strong>Href:</strong> {item.href}
                          </div>
                          <div>
                            <strong>Alt (EN):</strong> {item.translations.find((t) => t.language === "en")?.alt}
                          </div>
                          <div>
                            <strong>Alt (VI):</strong> {item.translations.find((t) => t.language === "vi")?.alt}
                          </div>
                          <span className={`status-badge ${item.is_active ? "active" : "inactive"}`}>{item.is_active ? "Active" : "Inactive"}</span>
                        </div>
                        <div className="item-actions">
                          <button className="btn-edit" onClick={() => handleEditItem(item, section.id)}>
                            Edit
                          </button>
                          <button className="btn-delete" onClick={() => handleDeleteItem(item.id)}>
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-items">No items in this section</div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LogostripManager;
